import React, { useMemo, useState } from 'react';
import { Search, Loader2, ChevronDown } from 'lucide-react';

export default function ManualScrapingView() {
  const [sourceType, setSourceType] = useState('urls');
  const [urls, setUrls] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);

  const handleExecute = async () => {
    setIsLoading(true);
    setExecutionResult(null);
    if (sourceType === 'urls') {
      try {
        const urlList = urls.split('\n').filter((u) => u.trim() !== '');
        if (urlList.length === 0) throw new Error('Please enter at least one valid URL.');
        const response = await fetch(
          'https://ai.supertrends.com/uat/description-generator/execute',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ urls: urlList, saveToDB: true }),
          }
        );
        if (!response.ok) throw new Error(`API Error: ${response.status}`);
        const data = await response.json();
        setExecutionResult({ results: Array.isArray(data) ? data : [data] });
      } catch (error) {
        setExecutionResult({ error: error.message });
      } finally {
        setIsLoading(false);
        setUrls('');
      }
    } else {
      setTimeout(() => {
        setExecutionResult({
          message: `Mock search for "${searchQuery}" successful.`,
        });
        setIsLoading(false);
        setSearchQuery('');
      }, 2000);
    }
  };

  const isButtonDisabled = useMemo(
    () =>
      isLoading ||
      (sourceType === 'urls' && urls.trim() === '') ||
      (sourceType === 'search' && searchQuery.trim() === ''),
    [isLoading, sourceType, urls, searchQuery]
  );

  return (
    <>
      <div className="flex flex-col gap-4">
        {sourceType === 'urls' ? (
          <div className="flex flex-col gap-2">
            <label className="block text-sm font-medium text-gray-400">
              Enter URLs (one per line)
            </label>
            <textarea
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              placeholder="https://example.com/page1&#10;https://example.com/page2"
              className="w-full h-36 bg-gray-700 border border-gray-600 rounded-lg py-2 pl-4 pr-4 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Enter Search Query
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="e.g., 'latest AI research papers'"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>
        )}
        <div className="flex items-center gap-4">
          <button
            onClick={handleExecute}
            disabled={isButtonDisabled}
            className="w-full md:w-auto bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center transition-all"
          >
            {isLoading ? <Loader2 className="animate-spin mr-2" /> : <Search className="mr-2" />}{' '}
            Execute
          </button>
          <div className="relative">
            <select
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-4 appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="urls">By URLs</option>
              <option value="search">By Search</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>
      <div className="mt-8 flex-grow flex flex-col min-h-[300px]">
        <h3 className="text-xl font-semibold text-gray-300 mb-4">Result Data</h3>
        <div className="bg-gray-900/50 p-4 rounded-lg flex-grow overflow-y-auto">
          {executionResult ? (
            <div>
              {executionResult.error && (
                <p className="text-red-400">Error: {executionResult.error}</p>
              )}
              {executionResult.message && (
                <p className="text-green-400">{executionResult.message}</p>
              )}
              {executionResult.results?.map((item, index) => (
                <div
                  key={index}
                  className="mt-4 pt-4 border-t border-gray-600 first:mt-0 first:pt-0 first:border-t-0"
                >
                  {item.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg font-bold text-cyan-400 hover:text-cyan-300 hover:underline"
                    >
                      {item.title}
                    </a>
                  ) : (
                    <h4 className="text-lg font-bold text-white">{item.title}</h4>
                  )}
                  <p className="text-gray-300 text-sm mt-2">{item.body}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 h-full flex items-center justify-center">
              <p>Execute a task to see results.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

